import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  RestaurantMenu,
  Close,
  Login,
  ArrowForward,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { Link, useNavigate, useLocation } from "react-router-dom";

import "./navbar.css";

const Navbar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navItems = [
    { title: "Home", path: "/" },
    { title: "Restaurants", path:"/restaurants"},
    { title: "Features", path: "/features" },
    { title: "Pricing", path: "/pricing" },
    { title: "About", path: "/about" },
    { title: "Contact", path: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const isActiveLink = (path) => {
    return location.pathname === path;
  };

  // Handle navigation with smooth scroll to top
  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (drawerOpen) toggleDrawer();
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        className={scrolled ? "navbar navbar-scroll" : "navbar"}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters className="toolbar">
            {/* Logo */}
            <Box 
              className="logo-section" 
              onClick={() => handleNavigation("/")}
            >
              <RestaurantMenu className="logo-icon" />
              <Typography variant="h5" className="logo-text">
                Restaurant<span>Wala</span>
              </Typography>
            </Box>

            {/* Desktop Menu */}
            {!isMobile && (
              <>
                <Box className="nav-links">
                  {navItems.map((item) => (
                    <Link
                      key={item.title}
                      to={item.path}
                      className={`nav-link ${isActiveLink(item.path) ? "active-link" : ""}`}
                      onClick={() => handleNavigation(item.path)}
                    >
                      {item.title}
                    </Link>
                  ))}
                </Box>

                {/* Right Buttons */}
                <Box className="right-buttons">
                  <Button
                    startIcon={<Login />}
                    className="login-btn"
                    onClick={() => handleNavigation("/login")}
                  >
                    Login
                  </Button>
                  <Button
                    endIcon={<ArrowForward />}
                    className="trial-btn"
                    onClick={() => handleNavigation("/register")}
                  >
                    Start Free Trial
                  </Button>
                </Box>
              </>
            )}

            {/* Mobile Right */}
            {isMobile && (
              <Box className="mobile-right">
                <Button
                  className="mobile-trial-btn"
                  onClick={() => handleNavigation("/register")}
                >
                  Start Free
                </Button>
                <IconButton onClick={toggleDrawer} className="mobile-menu-icon">
                  <MenuIcon />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={toggleDrawer}
          PaperProps={{ className: "mobile-drawer" }}
        >
          {/* Drawer Header */}
          <Box className="drawer-header">
            <Box
              className="logo-section"
              onClick={() => handleNavigation("/")}
            >
              <RestaurantMenu className="logo-icon" />
              <Typography variant="h5" className="logo-text">
                Restaurant<span>Wala</span>
              </Typography>
            </Box>
            <IconButton onClick={toggleDrawer} className="drawer-close-btn">
              <Close />
            </IconButton>
          </Box>

          <Divider />

          {/* Drawer Menu */}
          <List className="drawer-list">
            {navItems.map((item) => (
              <ListItemButton
                key={item.title}
                component={Link}
                to={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`drawer-item ${isActiveLink(item.path) ? "Mui-selected" : ""}`}
                selected={isActiveLink(item.path)}
              >
                <ListItemText primary={item.title} />
              </ListItemButton>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Drawer Buttons */}
          <Box className="drawer-buttons">
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Login />}
              className="drawer-login-btn"
              onClick={() => handleNavigation("/login")}
            >
              Login
            </Button>
            <Button
              fullWidth
              variant="contained"
              endIcon={<ArrowForward />}
              className="drawer-trial-btn"
              onClick={() => handleNavigation("/register")}
            >
              Start Free Trial
            </Button>
          </Box>
        </Drawer>
      </AppBar>

      {/* Navbar Spacer */}
      <Toolbar />
    </>
  );
};

export default Navbar;